// Imports
import { StripeError } from '@stripe/stripe-js';
import uniqueId from 'lodash/uniqueId';
import { lazy, Suspense, useRef } from 'react';
import isScreenshot from '../../../../../utils/is-screenshot';
import BaseFieldProps from '../../../../../foundation/front-end/components/forms/fields/base-field-props';
import CardContainer from './container';
import SuspenseFallback from './suspense-fallback';


// Lazily-loaded Card component
const StripeCard = lazy(() => import('./stripe'));


// Define the accepted props
export interface Props extends BaseFieldProps {
	/** A function that provides the getter for the field to be bound, usually to a `ref`. */
	bindGetter?: (getter: () => Promise<string>) => void;
	
	
	/** Whether the field should be disabled. Defaults to `false`. */
	disabled?: boolean;
	
	
	/** A function that provides custom handling for Stripe errors. The function is passed the error and a setter function for displaying an error message beneath the field as its parameters. */
	onStripeError?: (error: StripeError, setStripeError: React.Dispatch<React.SetStateAction<string>>) => void;
}


// Function component
const Card: React.FC<Props> = ({ bindGetter, onStripeError, ...props }) => {
	// Generate unique ID
	const uniqueID = useRef(uniqueId('card-field-'));
	
	
	// Don't load Stripe in snapshots
	if (isScreenshot()) {
		return (
			<CardContainer uniqueID={uniqueID} {...props}>
				<input id={uniqueID.current} aria-describedby={`error-field-${uniqueID.current}`} />
			</CardContainer>
		);
	}
	
	
	// Return JSX
	return (
		<Suspense fallback={<SuspenseFallback uniqueID={uniqueID} {...props} />}>
			<StripeCard bindGetter={bindGetter} onStripeError={onStripeError} uniqueID={uniqueID} {...props} />
		</Suspense>
	);
};

export default Card;
